/**
 * The hand-authored upgrade offers shown on /products and reused by locked upsell screens.
 *
 * Kept outside the route so every price-bearing upgrade surface reads the same source of
 * truth. The redesigned billing page introduced names, prices, badges and copy that no
 * longer match the older entitlement catalogue in data/products.ts; the locked Studio,
 * Composer+, Publisher, Vault and Commercial License screens all need this newer marketing
 * layer rather than the older product rows.
 */
import {
  BadgeCheck,
  Boxes,
  Disc3,
  Dna,
  FileMusic,
  Infinity as InfinityIcon,
  Layers,
  Package,
  Radio,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import type { CheckoutProductId, CheckoutProductOwnership, Product } from '../types/domain';
import { paths } from '../routes/paths';
import { VERIFIED_SIGNUP_CREDITS } from '../lib/credits';
import { UNLIMITED_FAIR_USE } from './fairUse';

export type UpgradeOfferRole = 'entry' | 'bundle' | 'fastpass' | 'downstream' | 'addon';
export type WholePackageCheckoutId = 'complete-creator-bundle' | 'fastpass';

export interface UpgradeProduct {
  name: string;
  tag: string;
  price: number;
  desc: string;
  features: string[];
  icon: NonNullable<Product['icon']>;
  priceDisplay?: string;
  priceMeta?: string;
  priceMetaBold?: string;
  billingNote?: string;
  priceSummary?: string;
  trial?: { headline: string; terms: string };
  featureNote?: string;
}

export interface UpgradeOffer {
  checkoutId: CheckoutProductId;
  product: UpgradeProduct;
  role: UpgradeOfferRole;
  navTargets?: readonly string[];
  recommended?: boolean;
  recommendedLabel?: string;
  highlyRecommended?: boolean;
  supportingBadgeLabel?: string;
  highlightVariant?: 'primary' | 'secondary';
  layout?: 'default' | 'addon-inline';
  autoOwnedBy?: readonly WholePackageCheckoutId[];
}

export interface ResolveUpgradeOffersInput {
  checkoutOwnership?: CheckoutProductOwnership;
}

export interface ResolvedUpgradeOffer extends UpgradeOffer {
  owned: boolean;
  visible: boolean;
  buyable: boolean;
}

export const UPGRADE_OFFERS: readonly UpgradeOffer[] = [
  {
    checkoutId: 'frontend',
    role: 'entry',
    product: {
      name: 'Lyrixsa AI Commercial Creator Edition',
      tag: 'Standalone',
      price: 27,
      icon: FileMusic,
      desc: 'Create songs from text, Spotify links, images and prompts. Build and manage a commercial-ready music catalogue.',
      features: [
        'Create original AI songs from text',
        `Includes ${VERIFIED_SIGNUP_CREDITS} generation credits`,
        'Create music from any Spotify song link',
        'Turn images into music',
        'Generate songs from creative ideas and prompts',
        'Explore different music styles and genres',
        'Create music for content, projects and commercial use',
        'Download and manage generated songs',
        'Build an original music catalogue',
        'Access the Lyrixsa AI music creation dashboard',
      ],
    },
    autoOwnedBy: ['complete-creator-bundle', 'fastpass'],
    recommended: true,
    recommendedLabel: 'Entry',
    highlightVariant: 'secondary',
  },
  {
    checkoutId: 'complete-creator-bundle',
    role: 'bundle',
    product: {
      name: 'Complete Creator Bundle',
      tag: 'Standalone',
      price: 317,
      icon: Boxes,
      desc: 'A bundled creator experience containing the major Lyrixsa AI creator upgrades.',
      features: [],
    },
    highlyRecommended: true,
    supportingBadgeLabel: 'Advanced Entry',
    highlightVariant: 'primary',
  },
  {
    checkoutId: 'fastpass',
    role: 'fastpass',
    product: {
      name: 'Lyrixsa AI Fastpass',
      tag: 'Standalone',
      price: 317,
      icon: Zap,
      desc: 'The discounted path to the major Lyrixsa AI upgrades.',
      features: [
        'Unlock the major creator upgrades',
        'Access launch bonuses where applicable',
        'Get the major creator features in one package',
        'Avoid purchasing major upgrades individually',
        'Faster path to the complete Lyrixsa creator experience',
      ],
    },
    recommended: true,
    supportingBadgeLabel: 'ADVANCED ENTRY',
    highlightVariant: 'primary',
  },
  {
    checkoutId: 'professional',
    role: 'downstream',
    product: {
      name: 'Lyrixsa AI Professional',
      tag: 'OTO 1',
      price: 37,
      icon: ShieldCheck,
      desc: 'For users who want a more advanced and professional-oriented music creation experience.',
      features: [
        'Higher-quality music creation',
        'More professional results',
        'Professional song-generation workflow',
        'Create more release-ready songs',
        'Professional-oriented creation capabilities',
        'Designed for serious music creators',
      ],
    },
    autoOwnedBy: ['complete-creator-bundle', 'fastpass'],
  },
  {
    checkoutId: 'publisher-monetization',
    role: 'downstream',
    product: {
      name: 'Lyrixsa AI Publisher & Song Monetization',
      tag: 'OTO 2',
      price: 297,
      priceMeta: 'one-time',
      icon: Radio,
      desc: 'Create → Prepare → Publish → Monetize',
      features: [
        'Access: One-time Publisher access',
        'Included: 10 Publisher Credits',
        'Publisher Credit cost: 1 per submitted track',
        'Annual Subscription: No',
        'Publish eligible songs',
        'Prepare releases for distribution',
        'Manage music releases',
        'Distribution workflow',
        'Spotify, Apple Music, YouTube Music, Amazon Music, Tidal, Deezer, TikTok, Instagram/Facebook, Boomplay, Tencent/QQ Music',
        'Other supported platforms',
        'Manage releases from one dashboard',
        'Build a music catalogue',
        'Manage publishing information',
        'Monetization workflow',
      ],
    },
    navTargets: [paths.publisher],
    autoOwnedBy: ['complete-creator-bundle', 'fastpass'],
  },
  {
    checkoutId: 'music-dna-composer',
    role: 'downstream',
    product: {
      name: 'Music DNA & Genre Mixer (AI Composer)',
      tag: 'OTO 3',
      price: 67,
      icon: Dna,
      desc: 'Analyze songs to uncover their musical DNA and defining traits. Mix genres to explore hybrid styles and new creative directions.',
      features: [
        'Analyze the musical characteristics of a song',
        'Discover the underlying musical DNA',
        'Analyze musical style',
        'Identify characteristics that define a sound',
        'Use musical insights for creative inspiration',
        'Mix different musical genres',
        'Experiment with hybrid styles',
        'Explore unusual genre combinations',
        'Create unique musical directions',
        'Discover new sounds',
        'Develop different musical identities',
        'Experiment with creative stylistic combinations',
      ],
    },
    navTargets: [paths.dna, paths.genreMixer],
    autoOwnedBy: ['complete-creator-bundle', 'fastpass'],
  },
  {
    checkoutId: 'studio-card',
    role: 'downstream',
    product: {
      name: 'Lyrixsa AI Studio',
      tag: 'OTO 4',
      price: 197,
      icon: Disc3,
      desc: 'The advanced music production & editing workspace',
      features: [
        'Extend, remix, replace a specific song section, modify',
        'Create alternate versions',
        'Rework sections',
        'Continue an existing song',
        'Split songs into stems',
        'Stem Controls, stem downloads',
        'Audio editing: pitch, tempo, mastering, reverb',
        'Mix your song',
        'Work with individual song elements',
        'Refine music',
        'Create production variations',
        'Upload songs',
        'Manage songs inside Studio',
        'Work on existing music',
        'Create multiple versions',
        'Export WAV/MP3',
      ],
    },
    navTargets: [paths.studio],
    autoOwnedBy: ['complete-creator-bundle', 'fastpass'],
  },
  {
    checkoutId: 'unlimited-creation-publishing',
    role: 'downstream',
    product: {
      name: 'Unlimited Song Creation & Expanded Publisher Monetization',
      tag: 'OTO 5',
      price: 497,
      priceMeta: 'yearly',
      icon: InfinityIcon,
      desc: 'Create with a monthly allowance. Publish up to 25 tracks',
      features: [
        'Unlimited music creation',
        'Included: 25 Publisher Credits',
        'Create repeatedly',
        'Publish eligible songs',
        'Build large music catalogues',
        'Designed for artists',
        'Designed for producers',
        'Designed for content creators',
        'Designed for power users',
      ],
      featureNote: `Fair use: ${UNLIMITED_FAIR_USE.monthlyCredits} creation credits/month.`,
    },
  },
  {
    checkoutId: 'commercial-certification',
    role: 'addon',
    product: {
      name: 'Commercial Creator Certification',
      tag: 'Add-on',
      price: 9.9,
      priceDisplay: '9.90',
      icon: BadgeCheck,
      desc: 'Proof of your commercial creator status for sales, distribution and monetization.',
      features: [
        'Documentation/confirmation of commercial creator status',
        'Designed to accompany the commercial creator experience',
      ],
    },
    layout: 'addon-inline',
  },
  {
    checkoutId: 'dfy-producer-vault',
    role: 'addon',
    product: {
      name: 'DFY Producer Vault',
      tag: 'Add-on',
      price: 27,
      icon: Package,
      desc: 'Ready-made prompts, templates and producer workflows to speed up music creation and release.',
      features: [
        'Ready-made music prompts',
        'Genre prompts',
        'Creative prompts',
        'Music production workflows',
        'Song creation ideas',
        'Templates',
        'Producer resources',
        'Music release resources',
        'Ready-to-use workflows',
        'Shortcuts for getting more from Lyrixsa',
      ],
    },
    layout: 'addon-inline',
    navTargets: [paths.vault],
  },
  {
    checkoutId: 'reseller',
    role: 'downstream',
    product: {
      name: 'Lyrixsa AI Reseller License',
      tag: 'OTO 6',
      price: 297,
      icon: Layers,
      desc: 'Build a business around Lyrixsa AI',
      features: [
        'Reseller certificate',
        'Reseller dashboard',
        'License verification',
        'Sales materials & email swipes',
        'Marketing graphics',
      ],
    },
  },
] as const;

/** The public product catalogue, derived from the offers rendered by `/upgrade`. */
export const CURRENT_PRODUCTS: readonly Product[] = UPGRADE_OFFERS.map(
  ({ checkoutId, product }) => ({
    id: checkoutId,
    name: product.name,
    tag: product.tag,
    price: product.price,
    desc: product.desc,
    features: product.features,
  })
);

const UPGRADE_OFFER_BY_ID = new Map<CheckoutProductId, UpgradeOffer>(
  UPGRADE_OFFERS.map((offer) => [offer.checkoutId, offer])
);

export function resolveUpgradeOffers({
  checkoutOwnership = {},
}: ResolveUpgradeOffersInput): ResolvedUpgradeOffer[] {
  const bundleOwned = checkoutOwnership['complete-creator-bundle'] === true;
  const fastpassOwned = checkoutOwnership.fastpass === true;
  const directOwned = new Map<CheckoutProductId, boolean>(
    UPGRADE_OFFERS.map((offer) => [offer.checkoutId, checkoutOwnership[offer.checkoutId] === true])
  );

  const entryOwned =
    directOwned.get('frontend') === true ||
    bundleOwned ||
    fastpassOwned ||
    UPGRADE_OFFERS.some(
      (offer) => offer.role === 'downstream' && directOwned.get(offer.checkoutId) === true
    );

  const missedFastpassWindow = UPGRADE_OFFERS.some(
    (offer) => offer.role === 'downstream' && directOwned.get(offer.checkoutId) === true
  );

  return UPGRADE_OFFERS.map((offer) => {
    const ownedByWholePackage =
      offer.autoOwnedBy?.some((id) => checkoutOwnership[id] === true) ?? false;
    const owned =
      offer.checkoutId === 'complete-creator-bundle'
        ? bundleOwned
        : offer.checkoutId === 'fastpass'
          ? fastpassOwned
          : ownedByWholePackage || directOwned.get(offer.checkoutId) === true;

    const visible =
      offer.role === 'addon'
        ? true
        : offer.checkoutId === 'complete-creator-bundle'
          ? bundleOwned || (!entryOwned && !fastpassOwned)
          : offer.checkoutId === 'fastpass'
            ? fastpassOwned || (entryOwned && !bundleOwned && !missedFastpassWindow)
            : true;

    return {
      ...offer,
      owned,
      visible,
      buyable: visible && !owned,
    };
  });
}

export function upgradeOffer(checkoutId: CheckoutProductId): UpgradeOffer | undefined {
  return UPGRADE_OFFER_BY_ID.get(checkoutId);
}

export function formatUpgradeOfferPrice(
  product: Pick<UpgradeProduct, 'price' | 'priceDisplay'>
): string {
  return `$${product.priceDisplay ?? product.price}`;
}

export function formatUpgradeOfferPriceLine(
  product: Pick<UpgradeProduct, 'price' | 'priceDisplay' | 'priceMeta'>
): string {
  return `${formatUpgradeOfferPrice(product)} ${product.priceMeta ?? 'one-time'}`;
}
